import React from 'react'
import { fetcherText } from '@/utils/fetcher'
import useSwrImmutable from 'swr/immutable'
import { API_URL } from '@/utils/constants'
import useWallet from '@/utils/hooks/useWallet'
import Event from '@/utils/event'

const HASH_LENGTH = 8
// Plain-text tip height from our API (5s cache, CORS open); mempool.space was a third-party dependency here.
const HEIGHT_URL = `${API_URL}/blockheight`
const CONTROLLER_EVENT = 'bitmap-canvas'
export const HEADER_EVENT = 'bitmap-header'
// Escrow listing info for a bitmap
export interface BitmapListing {
	id: string
	inscriptionId: string
	satoshiPrice: number
	sellerAddress: string
	name: string
}

// Parse bitmap number from escrow name (e.g., "146997.bitmap" -> 146997)
export const parseBitmapNumber = (name: string): number | null => {
	if (!name) return null
	const match = name.match(/^(\d+)\.bitmap$/i)
	return match ? parseInt(match[1], 10) : null
}

// Build a Set of listed block numbers from escrow data
export const buildListedBlocksSet = (escrows: any[]): Set<number> => {
	const set = new Set<number>()
	if (!escrows) return set

	for (const escrow of escrows) {
		const num = parseBitmapNumber(escrow.name)
		if (num !== null) {
			set.add(num)
		}
	}
	return set
}

// Build a Map of block number -> listing info from escrow data
export const buildListingsMap = (escrows: any[]): Map<number, BitmapListing> => {
	const map = new Map<number, BitmapListing>()
	if (!escrows) return map

	for (const escrow of escrows) {
		const num = parseBitmapNumber(escrow.name)
		if (num !== null) {
			map.set(num, {
				id: escrow.id,
				inscriptionId: escrow.inscription_id,
				satoshiPrice: escrow.satoshi_price,
				sellerAddress: escrow.seller_address,
				name: escrow.name
			})
		}
	}
	return map
}


export type CanvasState = 'show-all' | 'for-sale' | 'my-lands'
type BitmapType = 'globe-icon' | 'market-icon'

export type CanvasControllerEvent = {
	disabled?: boolean
	selected?: any
	state: CanvasState
}

export type HeaderEvent = {
	tab?: CanvasState
	type?: BitmapType
	block?: number
	focusMyLands?: boolean
}

export const emitCanvasEvent = (e: CanvasControllerEvent) => Event.emit(CONTROLLER_EVENT, e)
export const emitHeaderEvent = (e: HeaderEvent) => Event.emit(HEADER_EVENT, e)

export default function useBitmap(slug?: string): {
	myMap: any
	numMap: any
	idMap: any
	escrowMap: any
	escrows: any
	height: number
	isLoading: boolean
} {
	// URLs kept as reference while the map/escrow SWR hooks are disabled below.
	// eslint-disable-next-line unused-imports/no-unused-vars
	const MAP_URL = `${API_URL}/v2/collection/${slug}/map`
	// eslint-disable-next-line unused-imports/no-unused-vars
	const ESCROW_URL = `${API_URL}/collection/${slug}/escrows`
	// Temporarily disabled - API is down
	// const { data, isLoading: mapLoading } = useSwr(MAP_URL, fetcher)
	const mapLoading = false

	const { data: walletData, isLoading: walletLoading } = useWallet()

	// Temporarily disabled - API is down
	// const { data: escrowData, isLoading: escrowLoading } = useSwr(ESCROW_URL, fetcher)
	const escrowData = null
	const escrowLoading = false

	const { data: tipData, isLoading: tipLoading } = useSwrImmutable(HEIGHT_URL, fetcherText)

	const isLoading = mapLoading || walletLoading || escrowLoading || tipLoading

	const height = parseInt((tipData as any), 10)

	// Generate mock data showing all blocks up to current height as claimed
	// Since all bitmaps are already inscribed
	const data = React.useMemo(() => {
		if (!height || isNaN(height)) return []

		const mockData = []
		for (let i = 0; i < height; i++) {
			// Create a mock inscription ID for each block
			mockData.push({
				num: i,
				id: `${i.toString().padStart(8, '0')}i0` // Mock inscription ID format
			})
		}
		return mockData
	}, [height])

	const [numMap, idMap] = React.useMemo(() => {
		let numMap = {}
		let idMap = {}

		;(data || []).map((e: any) => {
			;(numMap as any)[e.num] = e.id
			;(idMap as any)[e.id] = e.num
		})

		return [numMap, idMap]
	}, [data])

	const myMap = React.useMemo(() => {
		let map = {}

		;(walletData?.inscriptions || []).map((e: any) => {
			if (e?.collection?.slug === slug) {
				(map as any)[(idMap as any)[e.id.slice(0, HASH_LENGTH)]] = true
			}
		})

		return map
	}, [idMap, slug, walletData?.inscriptions])

	const escrowMap = React.useMemo(() => {
		let map = {}

		if (slug === 'natmap') {
			return map
		}

		;(escrowData || []).map((e: any) => {
			const id = (e as any).inscription_id.slice(0, HASH_LENGTH)
			;(map as any)[(idMap as any)[id]] = (e as any).satoshi_price
		})

		return map
	}, [slug, idMap])

	return { myMap, isLoading, escrowMap, escrows: escrowData, numMap, idMap, height }
}
